import { supabase } from '../lib/supabase';

export const documentRenewalBatchService = {
  // ============================================================================
  // RENEWAL BATCHES
  // ============================================================================

  async getAllRenewalBatches(filters = {}) {
    try {
      let query = supabase
        ?.from('document_renewal_batches')
        ?.select('*')
        ?.order('created_at', { ascending: false });

      if (filters?.batchStatus) {
        query = query?.eq('batch_status', filters?.batchStatus);
      }

      if (filters?.batchPriority) {
        query = query?.eq('batch_priority', filters?.batchPriority);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        batchName: row?.batch_name,
        batchDescription: row?.batch_description,
        batchStatus: row?.batch_status,
        batchPriority: row?.batch_priority,
        expirationWindowStart: row?.expiration_window_start,
        expirationWindowEnd: row?.expiration_window_end,
        documentTypes: row?.document_types || [],
        departments: row?.departments || [],
        totalDocuments: row?.total_documents,
        processedDocuments: row?.processed_documents,
        failedDocuments: row?.failed_documents,
        currentWorkflowStep: row?.current_workflow_step,
        workflowProgressPercentage: row?.workflow_progress_percentage,
        autoProcessEnabled: row?.auto_process_enabled,
        autoNotifyEmployees: row?.auto_notify_employees,
        autoEscalateFailures: row?.auto_escalate_failures,
        createdBy: row?.created_by,
        createdAt: row?.created_at,
        submittedForReviewAt: row?.submitted_for_review_at,
        approvedBy: row?.approved_by,
        approvedAt: row?.approved_at,
        startedProcessingAt: row?.started_processing_at,
        completedAt: row?.completed_at,
        cancelledAt: row?.cancelled_at,
        cancellationReason: row?.cancellation_reason,
        metadata: row?.metadata,
        updatedAt: row?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error getting renewal batches:', error);
      throw error;
    }
  },

  async getRenewalBatchById(batchId) {
    try {
      const { data, error } = await supabase
        ?.from('document_renewal_batches')
        ?.select('*')
        ?.eq('id', batchId)
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        batchName: data?.batch_name,
        batchDescription: data?.batch_description,
        batchStatus: data?.batch_status,
        batchPriority: data?.batch_priority,
        expirationWindowStart: data?.expiration_window_start,
        expirationWindowEnd: data?.expiration_window_end,
        documentTypes: data?.document_types || [],
        departments: data?.departments || [],
        totalDocuments: data?.total_documents,
        processedDocuments: data?.processed_documents,
        failedDocuments: data?.failed_documents,
        currentWorkflowStep: data?.current_workflow_step,
        workflowProgressPercentage: data?.workflow_progress_percentage,
        autoProcessEnabled: data?.auto_process_enabled,
        autoNotifyEmployees: data?.auto_notify_employees,
        autoEscalateFailures: data?.auto_escalate_failures,
        createdBy: data?.created_by,
        createdAt: data?.created_at,
        submittedForReviewAt: data?.submitted_for_review_at,
        approvedBy: data?.approved_by,
        approvedAt: data?.approved_at,
        startedProcessingAt: data?.started_processing_at,
        completedAt: data?.completed_at,
        metadata: data?.metadata
      };
    } catch (error) {
      console.error('Error getting renewal batch:', error);
      throw error;
    }
  },

  async createRenewalBatch(batch) {
    try {
      const { data, error } = await supabase
        ?.from('document_renewal_batches')
        ?.insert({
          batch_name: batch?.batchName,
          batch_description: batch?.batchDescription,
          batch_status: batch?.batchStatus || 'draft',
          batch_priority: batch?.batchPriority || 'medium',
          expiration_window_start: batch?.expirationWindowStart,
          expiration_window_end: batch?.expirationWindowEnd,
          document_types: batch?.documentTypes || [],
          departments: batch?.departments || [],
          auto_process_enabled: batch?.autoProcessEnabled ?? true,
          auto_notify_employees: batch?.autoNotifyEmployees ?? true,
          auto_escalate_failures: batch?.autoEscalateFailures ?? true,
          created_by: batch?.createdBy,
          metadata: batch?.metadata || {}
        })
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        batchName: data?.batch_name,
        batchStatus: data?.batch_status
      };
    } catch (error) {
      console.error('Error creating renewal batch:', error);
      throw error;
    }
  },

  async updateRenewalBatch(batchId, updates) {
    try {
      const updateData = {
        updated_at: new Date()?.toISOString()
      };

      if (updates?.batchName !== undefined) updateData.batch_name = updates?.batchName;
      if (updates?.batchDescription !== undefined) updateData.batch_description = updates?.batchDescription;
      if (updates?.batchStatus !== undefined) updateData.batch_status = updates?.batchStatus;
      if (updates?.batchPriority !== undefined) updateData.batch_priority = updates?.batchPriority;
      if (updates?.approvedBy !== undefined) updateData.approved_by = updates?.approvedBy;
      if (updates?.approvedAt !== undefined) updateData.approved_at = updates?.approvedAt;
      if (updates?.startedProcessingAt !== undefined) updateData.started_processing_at = updates?.startedProcessingAt;
      if (updates?.completedAt !== undefined) updateData.completed_at = updates?.completedAt;
      if (updates?.cancelledAt !== undefined) updateData.cancelled_at = updates?.cancelledAt;
      if (updates?.cancellationReason !== undefined) updateData.cancellation_reason = updates?.cancellationReason;

      const { data, error } = await supabase
        ?.from('document_renewal_batches')
        ?.update(updateData)
        ?.eq('id', batchId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        batchStatus: data?.batch_status
      };
    } catch (error) {
      console.error('Error updating renewal batch:', error);
      throw error;
    }
  },

  async deleteBatch(batchId) {
    try {
      const { error } = await supabase
        ?.from('document_renewal_batches')
        ?.delete()
        ?.eq('id', batchId);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error deleting batch:', error);
      throw error;
    }
  },

  // ============================================================================
  // BATCH ITEMS
  // ============================================================================

  async getBatchItems(batchId) {
    try {
      const { data, error } = await supabase
        ?.from('document_renewal_batch_items')
        ?.select(`
          *,
          employees (
            employee_number,
            first_name,
            last_name,
            department,
            email
          )
        `)
        ?.eq('batch_id', batchId)
        ?.order('created_at', { ascending: true });

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        batchId: row?.batch_id,
        documentId: row?.document_id,
        employeeId: row?.employee_id,
        employeeNumber: row?.employees?.employee_number,
        employeeName: `${row?.employees?.first_name} ${row?.employees?.last_name}`,
        department: row?.employees?.department,
        email: row?.employees?.email,
        documentType: row?.document_type,
        documentName: row?.document_name,
        currentExpiryDate: row?.current_expiry_date,
        daysUntilExpiry: row?.days_until_expiry,
        expirationStatus: row?.expiration_status,
        itemStatus: row?.item_status,
        processingStartedAt: row?.processing_started_at,
        processingCompletedAt: row?.processing_completed_at,
        newExpiryDate: row?.new_expiry_date,
        renewalReferenceNumber: row?.renewal_reference_number,
        renewalNotes: row?.renewal_notes,
        errorMessage: row?.error_message,
        retryCount: row?.retry_count,
        lastRetryAt: row?.last_retry_at,
        metadata: row?.metadata,
        createdAt: row?.created_at
      })) || [];
    } catch (error) {
      console.error('Error getting batch items:', error);
      throw error;
    }
  },

  async addDocumentsToBatch(batchId, documentIds) {
    try {
      // Get document details
      const { data: documents, error: docError } = await supabase
        ?.from('employee_documents')
        ?.select(`
          id,
          employee_id,
          document_type,
          document_name,
          expiry_date,
          days_until_expiry,
          expiration_status
        `)
        ?.in('id', documentIds);

      if (docError) throw docError;

      // Create batch items
      const items = documents?.map(doc => ({
        batch_id: batchId,
        document_id: doc?.id,
        employee_id: doc?.employee_id,
        document_type: doc?.document_type,
        document_name: doc?.document_name,
        current_expiry_date: doc?.expiry_date,
        days_until_expiry: doc?.days_until_expiry,
        expiration_status: doc?.expiration_status
      }));

      const { data, error } = await supabase
        ?.from('document_renewal_batch_items')
        ?.insert(items)
        ?.select();

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        documentId: row?.document_id
      })) || [];
    } catch (error) {
      console.error('Error adding documents to batch:', error);
      throw error;
    }
  },

  async updateBatchItem(itemId, updates) {
    try {
      const updateData = {
        updated_at: new Date()?.toISOString()
      };

      if (updates?.itemStatus !== undefined) updateData.item_status = updates?.itemStatus;
      if (updates?.processingStartedAt !== undefined) updateData.processing_started_at = updates?.processingStartedAt;
      if (updates?.processingCompletedAt !== undefined) updateData.processing_completed_at = updates?.processingCompletedAt;
      if (updates?.newExpiryDate !== undefined) updateData.new_expiry_date = updates?.newExpiryDate;
      if (updates?.renewalReferenceNumber !== undefined) updateData.renewal_reference_number = updates?.renewalReferenceNumber;
      if (updates?.renewalNotes !== undefined) updateData.renewal_notes = updates?.renewalNotes;
      if (updates?.errorMessage !== undefined) updateData.error_message = updates?.errorMessage;

      const { data, error } = await supabase
        ?.from('document_renewal_batch_items')
        ?.update(updateData)
        ?.eq('id', itemId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        itemStatus: data?.item_status
      };
    } catch (error) {
      console.error('Error updating batch item:', error);
      throw error;
    }
  },

  async removeItemFromBatch(itemId) {
    try {
      const { error } = await supabase
        ?.from('document_renewal_batch_items')
        ?.delete()
        ?.eq('id', itemId);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error removing item from batch:', error);
      throw error;
    }
  },

  // ============================================================================
  // WORKFLOW STEPS
  // ============================================================================

  async getWorkflowSteps() {
    try {
      const { data, error } = await supabase
        ?.from('renewal_workflow_steps')
        ?.select('*')
        ?.eq('is_active', true)
        ?.order('step_order', { ascending: true });

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        stepOrder: row?.step_order,
        stepName: row?.step_name,
        stepDescription: row?.step_description,
        isAutomated: row?.is_automated,
        automationScript: row?.automation_script,
        requiresApproval: row?.requires_approval,
        approverRole: row?.approver_role,
        estimatedDurationMinutes: row?.estimated_duration_minutes,
        timeoutMinutes: row?.timeout_minutes,
        dependsOnStepOrder: row?.depends_on_step_order,
        isActive: row?.is_active
      })) || [];
    } catch (error) {
      console.error('Error getting workflow steps:', error);
      throw error;
    }
  },

  async getWorkflowExecutions(batchId) {
    try {
      const { data, error } = await supabase
        ?.from('renewal_workflow_executions')
        ?.select('*')
        ?.eq('batch_id', batchId)
        ?.order('step_order', { ascending: true });

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        batchId: row?.batch_id,
        workflowStepId: row?.workflow_step_id,
        stepOrder: row?.step_order,
        stepName: row?.step_name,
        stepStatus: row?.step_status,
        startedAt: row?.started_at,
        completedAt: row?.completed_at,
        durationSeconds: row?.duration_seconds,
        itemsProcessed: row?.items_processed,
        itemsSucceeded: row?.items_succeeded,
        itemsFailed: row?.items_failed,
        requiresApproval: row?.requires_approval,
        approvedBy: row?.approved_by,
        approvedAt: row?.approved_at,
        rejectionReason: row?.rejection_reason,
        errorMessage: row?.error_message,
        errorDetails: row?.error_details,
        metadata: row?.metadata
      })) || [];
    } catch (error) {
      console.error('Error getting workflow executions:', error);
      throw error;
    }
  },

  async startWorkflowStep(batchId, stepId, stepOrder, stepName) {
    try {
      const { data, error } = await supabase
        ?.from('renewal_workflow_executions')
        ?.insert({
          batch_id: batchId,
          workflow_step_id: stepId,
          step_order: stepOrder,
          step_name: stepName,
          step_status: 'in_progress',
          started_at: new Date()?.toISOString()
        })
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        stepStatus: data?.step_status
      };
    } catch (error) {
      console.error('Error starting workflow step:', error);
      throw error;
    }
  },

  async completeWorkflowStep(executionId, results) {
    try {
      const { data, error } = await supabase
        ?.from('renewal_workflow_executions')
        ?.update({
          step_status: 'completed',
          completed_at: new Date()?.toISOString(),
          items_processed: results?.itemsProcessed || 0,
          items_succeeded: results?.itemsSucceeded || 0,
          items_failed: results?.itemsFailed || 0,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', executionId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        stepStatus: data?.step_status
      };
    } catch (error) {
      console.error('Error completing workflow step:', error);
      throw error;
    }
  },

  // ============================================================================
  // BATCH ACTIONS
  // ============================================================================

  async submitBatchForReview(batchId, submittedBy) {
    try {
      const { data, error } = await supabase
        ?.from('document_renewal_batches')
        ?.update({
          batch_status: 'pending_review',
          submitted_for_review_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', batchId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        batchStatus: data?.batch_status
      };
    } catch (error) {
      console.error('Error submitting batch for review:', error);
      throw error;
    }
  },

  async approveBatch(batchId, approvedBy) {
    try {
      const { data, error } = await supabase
        ?.from('document_renewal_batches')
        ?.update({
          batch_status: 'approved',
          approved_by: approvedBy,
          approved_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', batchId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        batchStatus: data?.batch_status
      };
    } catch (error) {
      console.error('Error approving batch:', error);
      throw error;
    }
  },

  async startBatchProcessing(batchId) {
    try {
      const { data, error } = await supabase
        ?.from('document_renewal_batches')
        ?.update({
          batch_status: 'processing',
          started_processing_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', batchId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        batchStatus: data?.batch_status
      };
    } catch (error) {
      console.error('Error starting batch processing:', error);
      throw error;
    }
  },

  async completeBatch(batchId) {
    try {
      const { data, error } = await supabase
        ?.from('document_renewal_batches')
        ?.update({
          batch_status: 'completed',
          completed_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', batchId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        batchStatus: data?.batch_status
      };
    } catch (error) {
      console.error('Error completing batch:', error);
      throw error;
    }
  },

  async cancelBatch(batchId, reason) {
    try {
      const { data, error } = await supabase
        ?.from('document_renewal_batches')
        ?.update({
          batch_status: 'cancelled',
          cancelled_at: new Date()?.toISOString(),
          cancellation_reason: reason,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', batchId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        batchStatus: data?.batch_status
      };
    } catch (error) {
      console.error('Error cancelling batch:', error);
      throw error;
    }
  },

  // ============================================================================
  // AUTO-CREATE BATCHES
  // ============================================================================

  async autoCreateRenewalBatches() {
    try {
      const { error } = await supabase?.rpc('auto_create_renewal_batches');

      if (error) throw error;

      return { success: true, message: 'Renewal batches created successfully' };
    } catch (error) {
      console.error('Error auto-creating renewal batches:', error);
      throw error;
    }
  },

  // ============================================================================
  // STATISTICS
  // ============================================================================

  async getBatchStatistics() {
    try {
      const { data, error } = await supabase
        ?.from('document_renewal_batches')
        ?.select('batch_status, batch_priority, total_documents, processed_documents, failed_documents');

      if (error) throw error;

      const stats = {
        totalBatches: data?.length || 0,
        draftBatches: data?.filter(b => b?.batch_status === 'draft')?.length || 0,
        pendingReviewBatches: data?.filter(b => b?.batch_status === 'pending_review')?.length || 0,
        approvedBatches: data?.filter(b => b?.batch_status === 'approved')?.length || 0,
        processingBatches: data?.filter(b => b?.batch_status === 'processing')?.length || 0,
        completedBatches: data?.filter(b => b?.batch_status === 'completed')?.length || 0,
        criticalBatches: data?.filter(b => b?.batch_priority === 'critical')?.length || 0,
        totalDocuments: data?.reduce((sum, b) => sum + (b?.total_documents || 0), 0),
        processedDocuments: data?.reduce((sum, b) => sum + (b?.processed_documents || 0), 0),
        failedDocuments: data?.reduce((sum, b) => sum + (b?.failed_documents || 0), 0)
      };

      return stats;
    } catch (error) {
      console.error('Error getting batch statistics:', error);
      throw error;
    }
  },

  // ============================================================================
  // REALTIME SUBSCRIPTIONS
  // ============================================================================

  subscribeToRenewalBatches(callback) {
    const channel = supabase
      ?.channel('renewal_batches_changes')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'document_renewal_batches'
        },
        callback
      )
      ?.subscribe();

    return channel;
  },

  unsubscribeFromRenewalBatches(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  }
};

export default documentRenewalBatchService;